/**
 * Link Discovery Modal Component
 * 
 * Handles URL discovery and link selection workflow for the "verify" crawl mode:
 * - Discovers links from a provided URL using the /discover-links endpoint
 * - Displays discovered links in an organized, selectable list
 * - Allows users to preview and select which links to process
 * - Provides filtering and search capabilities for large link lists
 * - Handles loading states, errors, and validation
 * 
 * Design Reference: Based on original HTML frontend link discovery functionality
 * Code Standards: TypeScript, React hooks, comprehensive error handling, accessibility
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { 
  Search, 
  Globe, 
  ExternalLink, 
  Check, 
  X, 
  Loader2, 
  AlertCircle,
  Filter,
  Eye,
  ChevronDown,
  ChevronRight,
  RefreshCw
} from 'lucide-react'

import { Button } from '../../ui/Button'
import { Input } from '../../ui/Input'
import { Modal } from '../../ui/Modal'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card'
import type { Chatbot } from '../../../types'
import { logger } from '../../../utils/logger'
import { cn } from '../../../utils/cn'
import { api } from '../../../services/api'

/**
 * Discovered link interface matching backend response from Crawl4AI
 */
interface DiscoveredLink {
  url: string
  text: string
  category: string
  selected?: boolean
  estimated_relevance?: string
  // Optional properties for UI compatibility
  title?: string
  description?: string
  status_code?: number
  content_type?: string
  size?: number
  depth?: number
  parent_url?: string
}

/**
 * Link discovery response interface
 */
interface LinkDiscoveryResponse {
  base_url: string
  discovered_links: DiscoveredLink[]
  total_found: number
  discovery_time: number
  status: 'success' | 'partial' | 'failed'
  message?: string
}

/**
 * Link selection state interface
 */
interface LinkSelection {
  [url: string]: boolean
}

/**
 * Filter options for discovered links
 */
interface LinkFilters {
  search: string
  contentType: string
  statusCode: string
  depth: number | null
  showSelected: boolean
}

/**
 * Props interface for LinkDiscoveryModal
 */
interface LinkDiscoveryModalProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Function to close the modal */
  onClose: () => void
  /** Initial URL to discover links from */
  initialUrl: string
  /** Selected chatbot for the discovery operation */
  selectedChatbot: Chatbot | null
  /** Callback when links are selected for processing */
  onLinksSelected: (selectedUrls: string[]) => void
  /** Optional callback when discovery completes */
  onDiscoveryComplete?: (result: LinkDiscoveryResponse) => void
  /** Whether to request Super Agent metadata suggestions */
  suggestMetadata?: boolean
}

/**
 * Default filter state
 */
const DEFAULT_FILTERS: LinkFilters = {
  search: '',
  contentType: 'all',
  statusCode: 'all',
  depth: null,
  showSelected: false
}

/**
 * Category options for filtering discovered links
 */
const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All Categories' },
  { value: 'support', label: 'Support' },
  { value: 'docs', label: 'Documentation' },
  { value: 'content', label: 'Content' },
  { value: 'info', label: 'Information' },
  { value: 'navigation', label: 'Navigation' },
  { value: 'general', label: 'General' }
] as const

/**
 * LinkDiscoveryModal Component
 * 
 * Complete URL discovery and link selection interface
 */
export const LinkDiscoveryModal: React.FC<LinkDiscoveryModalProps> = ({
  isOpen,
  onClose,
  initialUrl,
  selectedChatbot,
  onLinksSelected,
  onDiscoveryComplete,
  suggestMetadata = true
}) => {
  // ====================================
  // STATE MANAGEMENT
  // ====================================
  
  const [discoveryState, setDiscoveryState] = useState<{
    isDiscovering: boolean
    result: LinkDiscoveryResponse | null
    error: string | null
    progress: number
  }>({
    isDiscovering: false,
    result: null,
    error: null,
    progress: 0
  })
  
  const [linkSelection, setLinkSelection] = useState<LinkSelection>({})
  const [filters, setFilters] = useState<LinkFilters>(DEFAULT_FILTERS)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['category-support', 'category-docs', 'category-general']))
  
  // Refs for cleanup and abort control
  const abortControllerRef = useRef<AbortController | null>(null)
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // ====================================
  // EFFECTS
  // ====================================

  /**
   * Start discovery when modal opens with initial URL
   */
  useEffect(() => {
    if (isOpen && initialUrl && selectedChatbot && !discoveryState.result && !discoveryState.isDiscovering) {
      discoverLinks(initialUrl)
    }
  }, [isOpen, initialUrl, selectedChatbot])

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
      }
    }
  }, [])

  /**
   * Log component lifecycle events
   */
  useEffect(() => {
    if (isOpen) {
      logger.component('LinkDiscoveryModal', 'open', {
        initialUrl,
        hasResult: !!discoveryState.result
      })
    }
  }, [isOpen, initialUrl, discoveryState.result])

  // ====================================
  // LINK DISCOVERY LOGIC
  // ====================================

  /**
   * Discover links from the provided URL
   */
  const discoverLinks = useCallback(async (url: string) => {
    if (!selectedChatbot) {
      logger.error('Discovery attempted without selected chatbot', {
        component: 'LinkDiscoveryModal',
        action: 'discover_error',
        error: 'No chatbot selected'
      })
      setDiscoveryState({
        isDiscovering: false,
        result: null,
        error: 'Please select a chatbot first',
        progress: 0
      })
      return
    }

    try {
      logger.debug('Starting link discovery', {
        component: 'LinkDiscoveryModal',
        action: 'discover_start',
        url,
        chatbotId: selectedChatbot.id
      })

      // Setup abort controller for cancellation
      abortControllerRef.current = new AbortController()
      
      setDiscoveryState({
        isDiscovering: true,
        result: null,
        error: null,
        progress: 0
      })

      const response = await api.post('/discover-links', { 
        url,
        chatbot_id: selectedChatbot.id,
        suggest_metadata: suggestMetadata
      }, {
        signal: abortControllerRef.current.signal
      })

      logger.debug('Discovery session started', {
        component: 'LinkDiscoveryModal',
        action: 'session_start',
        response: response.data,
        status: response.status
      })

      const sessionData = response.data
      if (!sessionData.session_id) {
        throw new Error('No session ID received from server')
      }

      // Start polling for session status
      await pollDiscoveryStatus(sessionData.session_id)

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        logger.debug('Link discovery aborted', {
          component: 'LinkDiscoveryModal',
          action: 'discover_abort'
        })
        return
      }

      const errorMessage = error instanceof Error ? error.message : 'Link discovery failed'
      
      logger.error('Link discovery failed', {
        component: 'LinkDiscoveryModal',
        action: 'discover_error',
        error: errorMessage,
        url
      })

      setDiscoveryState({
        isDiscovering: false,
        result: null,
        error: errorMessage,
        progress: 0
      })
    }
  }, [selectedChatbot, onDiscoveryComplete])

  /**
   * Poll discovery session status until completion
   */
  const pollDiscoveryStatus = async (sessionId: string) => {
    const pollInterval = 1000 // Poll every second for faster updates
    const maxAttempts = 120 // Max 2 minutes
    let attempts = 0

    return new Promise<void>((resolve, reject) => {
      const poll = async () => {
        try {
          attempts++
          
          if (attempts > maxAttempts) {
            reject(new Error('Discovery session timeout - please try again'))
            return
          }

          if (abortControllerRef.current?.signal.aborted) {
            reject(new Error('Discovery aborted'))
            return
          }

          // Poll the backend for the actual session status
          try {
            const sessionResponse = await api.get(`/session-status/${sessionId}`)
            const sessionData = sessionResponse.data

            logger.debug('Discovery session poll', {
              component: 'LinkDiscoveryModal',
              action: 'session_poll',
              sessionId,
              attempts,
              status: sessionData.status,
              progress: sessionData.progress
            })

            // Update progress from backend
            if (sessionData.progress !== undefined) {
              setDiscoveryState(prev => ({
                ...prev,
                progress: sessionData.progress
              }))
            }

            if (sessionData.status === 'completed' && sessionData.result) {
              // Discovery completed successfully - use real backend results
              const backendResult = sessionData.result
              
              // Transform backend result to our expected format
              const linkDiscoveryResult: LinkDiscoveryResponse = {
                base_url: backendResult.main_url || initialUrl,
                discovered_links: backendResult.discovered_links || [],
                total_found: backendResult.total_links_found || 0,
                discovery_time: (attempts * pollInterval) / 1000, // Calculate from polling time
                status: 'success',
                message: sessionData.message || 'Link discovery completed successfully'
              }

              logger.debug('Link discovery completed with real results', {
                component: 'LinkDiscoveryModal',
                action: 'discover_complete_real',
                linksFound: linkDiscoveryResult.total_found,
                discoveredLinks: linkDiscoveryResult.discovered_links
              })

              setDiscoveryState({
                isDiscovering: false,
                result: linkDiscoveryResult,
                error: null,
                progress: 100
              })

              // Initialize selection state (select base URL by default)
              const initialSelection: LinkSelection = {}
              if (linkDiscoveryResult.discovered_links && linkDiscoveryResult.discovered_links.length > 0) {
                const baseLink = linkDiscoveryResult.discovered_links.find(link => link.url === initialUrl)
                if (baseLink) {
                  initialSelection[baseLink.url] = true
                }
              }
              setLinkSelection(initialSelection)

              onDiscoveryComplete?.(linkDiscoveryResult)
              
              // Clear the discovery session from backend to prevent 409 conflicts
              try {
                await api.post('/clear-discovery-session', {
                  session_id: sessionId,
                  chatbot_id: selectedChatbot?.id
                })
                logger.debug('Discovery session cleared successfully', {
                  component: 'LinkDiscoveryModal',
                  action: 'session_cleared',
                  sessionId
                })
              } catch (clearError) {
                logger.warn('Failed to clear discovery session', {
                  component: 'LinkDiscoveryModal',
                  action: 'clear_session_error',
                  error: clearError instanceof Error ? clearError.message : 'Unknown error',
                  sessionId
                })
                // Don't fail the discovery if session cleanup fails
              }
              
              resolve()
              return
            } else if (sessionData.status === 'error') {
              reject(new Error(sessionData.error || sessionData.message || 'Discovery failed'))
              return
            }

            // Continue polling if still in progress
            setTimeout(poll, pollInterval)

          } catch (sessionError) {
            logger.debug('Session polling error, continuing', {
              component: 'LinkDiscoveryModal',
              action: 'session_poll_error',
              error: sessionError instanceof Error ? sessionError.message : 'Unknown error',
              attempts
            })

            // If we can't access the session endpoint, continue polling with a fallback
            if (attempts > 15) { // After 15 seconds, create a fallback result
              logger.debug('Discovery polling timeout, using fallback result', {
                component: 'LinkDiscoveryModal',
                action: 'fallback_result',
                url: initialUrl
              })

              // Create a basic fallback result
              const fallbackResult: LinkDiscoveryResponse = {
                base_url: initialUrl,
                discovered_links: [{
                  url: initialUrl,
                  title: 'Main Page',
                  description: 'The main page you requested to discover links from',
                  status_code: 200,
                  content_type: 'text/html',
                  depth: 0
                }],
                total_found: 1,
                discovery_time: attempts,
                status: 'success',
                message: 'Discovery session completed. Showing main URL - full results may be available in backend logs.'
              }

              setDiscoveryState({
                isDiscovering: false,
                result: fallbackResult,
                error: null,
                progress: 100
              })

              const initialSelection: LinkSelection = {
                [initialUrl]: true
              }
              setLinkSelection(initialSelection)

              onDiscoveryComplete?.(fallbackResult)
              resolve()
              return
            }

            // Continue polling
            setTimeout(poll, pollInterval)
          }

        } catch (error) {
          logger.error('Discovery status polling error', {
            component: 'LinkDiscoveryModal',
            action: 'status_poll_error',
            error: error instanceof Error ? error.message : 'Unknown error'
          })
          
          reject(error)
        }
      }

      poll()
    })
  }

  /**
   * Start progress simulation for better UX
   */
  const startProgressSimulation = () => {
    let progress = 0
    progressIntervalRef.current = setInterval(() => {
      progress += Math.random() * 15
      if (progress >= 90) {
        progress = 90 // Cap at 90% until real completion
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current)
        }
      }
      setDiscoveryState(prev => ({ ...prev, progress }))
    }, 500)
  }

  /**
   * Stop progress simulation
   */
  const stopProgressSimulation = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }
  }

  /**
   * Cancel ongoing discovery
   */
  const cancelDiscovery = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    
    setDiscoveryState({
      isDiscovering: false,
      result: null,
      error: null,
      progress: 0
    })

    logger.debug('Link discovery cancelled by user', {
      component: 'LinkDiscoveryModal',
      action: 'user_cancel'
    })
  }

  /**
   * Retry discovery with the same URL
   */
  const retryDiscovery = () => {
    logger.debug('Retrying link discovery', {
      component: 'LinkDiscoveryModal',
      action: 'retry_discovery',
      url: initialUrl
    })
    
    discoverLinks(initialUrl)
  }

  // ====================================
  // LINK SELECTION LOGIC
  // ====================================

  /**
   * Toggle selection for a single link
   */
  const toggleLinkSelection = useCallback((url: string) => {
    setLinkSelection(prev => ({
      ...prev,
      [url]: !prev[url]
    }))

    logger.debug('Link selection toggled', {
      component: 'LinkDiscoveryModal',
      action: 'toggle_selection',
      url,
      isSelected: !linkSelection[url]
    })
  }, [linkSelection])

  /**
   * Select all filtered links
   */
  const selectAllFilteredLinks = () => {
    if (!Array.isArray(filteredLinks)) return
    
    const newSelection = { ...linkSelection }
    filteredLinks.forEach(link => {
      if (link && link.url) {
        newSelection[link.url] = true
      }
    })
    setLinkSelection(newSelection)

    logger.debug('All filtered links selected', {
      component: 'LinkDiscoveryModal',
      action: 'select_all_filtered',
      count: filteredLinks.length
    })
  }

  /**
   * Deselect all links
   */
  const deselectAllLinks = () => {
    setLinkSelection({})
    
    logger.debug('All links deselected', {
      component: 'LinkDiscoveryModal',
      action: 'deselect_all'
    })
  }

  /**
   * Get array of selected URLs
   */
  const selectedUrls = useMemo(() => {
    return Object.entries(linkSelection)
      .filter(([_, isSelected]) => isSelected)
      .map(([url]) => url)
  }, [linkSelection])

  // ====================================
  // FILTERING AND GROUPING
  // ====================================

  /**
   * Filter links based on current filter settings
   */
  const filteredLinks = useMemo(() => {
    if (!discoveryState.result || !Array.isArray(discoveryState.result.discovered_links)) return []

    let filtered = discoveryState.result.discovered_links

    // Apply search filter
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(link =>
        link.url.toLowerCase().includes(searchLower) ||
        link.text?.toLowerCase().includes(searchLower) ||
        link.category?.toLowerCase().includes(searchLower)
      )
    }

    // Apply category filter
    if (filters.contentType !== 'all') {
      filtered = filtered.filter(link => link.category === filters.contentType)
    }

    // Skip status code filter for Crawl4AI links (not applicable)
    // Skip depth filter for Crawl4AI links (not applicable)

    // Apply selection filter
    if (filters.showSelected) {
      filtered = filtered.filter(link => linkSelection[link.url])
    }

    return filtered
  }, [discoveryState.result, filters, linkSelection])

  /**
   * Group links by category for organized display
   */
  const groupedLinks = useMemo(() => {
    if (!Array.isArray(filteredLinks)) return []
    
    const groups = new Map<string, DiscoveredLink[]>()
    
    filteredLinks.forEach(link => {
      if (link && link.category && link.url) {
        const category = link.category
        if (!groups.has(category)) {
          groups.set(category, [])
        }
        groups.get(category)!.push(link)
      }
    })

    // Sort groups by category priority and links within each group by URL
    const categoryOrder = ['support', 'docs', 'content', 'info', 'navigation', 'general']
    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        const indexA = categoryOrder.indexOf(a)
        const indexB = categoryOrder.indexOf(b)
        const priorityA = indexA === -1 ? categoryOrder.length : indexA
        const priorityB = indexB === -1 ? categoryOrder.length : indexB
        return priorityA - priorityB
      })
      .map(([category, links]) => ({
        depth: category, // Use category as "depth" for display compatibility
        links: links.sort((a, b) => a.url.localeCompare(b.url))
      }))
  }, [filteredLinks])

  // ====================================
  // EVENT HANDLERS
  // ====================================

  /**
   * Handle filter changes
   */
  const updateFilter = useCallback(<K extends keyof LinkFilters>(
    key: K,
    value: LinkFilters[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    
    logger.debug('Filter updated', {
      component: 'LinkDiscoveryModal',
      action: 'filter_update',
      filterKey: key,
      filterValue: value
    })
  }, [])

  /**
   * Toggle group expansion
   */
  const toggleGroupExpansion = (category: string) => {
    const key = `category-${category}`
    const newExpanded = new Set(expandedGroups)
    
    if (newExpanded.has(key)) {
      newExpanded.delete(key)
    } else {
      newExpanded.add(key)
    }
    
    setExpandedGroups(newExpanded)
  }

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (discoveryState.isDiscovering) {
      cancelDiscovery()
    }
    
    // Reset state on close
    setDiscoveryState({
      isDiscovering: false,
      result: null,
      error: null,
      progress: 0
    })
    setLinkSelection({})
    setFilters(DEFAULT_FILTERS)
    setExpandedGroups(new Set(['category-support', 'category-docs', 'category-general']))
    
    onClose()
  }

  /**
   * Handle link selection confirmation
   */
  const handleConfirmSelection = async () => {
    const urls = selectedUrls
    
    logger.debug('Link selection confirmed', {
      component: 'LinkDiscoveryModal',
      action: 'confirm_selection',
      selectedCount: urls.length,
      urls
    })

    // Add a small delay to let the discovery session complete
    // This helps avoid 409 conflicts when immediately processing URLs after discovery
    setTimeout(() => {
      onLinksSelected(urls)
      handleClose()
    }, 500) // 500ms delay
  }

  // ====================================
  // RENDER HELPERS
  // ====================================

  /**
   * Render discovery progress with enhanced UI
   */
  const renderProgress = () => {
    if (!discoveryState.isDiscovering) return null

    return (
      <div className="py-8">
        <Card className="border-0 shadow-none bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-8 text-center space-y-6">
            {/* Animated Icon */}
            <div className="relative mx-auto w-20 h-20">
              <div className="absolute inset-0 bg-blue-100 rounded-full animate-pulse"></div>
              <div className="relative w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            </div>
            
            {/* Status Message */}
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-gray-900">
                Discovering Links
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Scanning <span className="font-medium text-blue-700">{initialUrl}</span> for available pages and content
              </p>
            </div>
            
            {/* Enhanced Progress Bar */}
            <div className="space-y-3 max-w-sm mx-auto">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 font-medium">Progress</span>
                <span className="text-blue-600 font-bold">{Math.round(discoveryState.progress)}%</span>
              </div>
              
              <div className="relative">
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 h-3 rounded-full transition-all duration-500 ease-out relative"
                    style={{ width: `${discoveryState.progress}%` }}
                  >
                    {discoveryState.progress > 10 && (
                      <div className="absolute inset-0 bg-white/20 animate-pulse rounded-full"></div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Progress Steps */}
              <div className="text-xs text-gray-500 space-y-1">
                <div className={cn(
                  "flex items-center gap-2 transition-all duration-300",
                  discoveryState.progress > 10 ? "text-blue-600" : ""
                )}>
                  {discoveryState.progress > 10 ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <div className="w-3 h-3 rounded-full border-2 border-current animate-pulse" />
                  )}
                  <span>Connecting to website</span>
                </div>
                
                <div className={cn(
                  "flex items-center gap-2 transition-all duration-300",
                  discoveryState.progress > 40 ? "text-blue-600" : "text-gray-400"
                )}>
                  {discoveryState.progress > 40 ? (
                    <Check className="w-3 h-3" />
                  ) : discoveryState.progress > 10 ? (
                    <div className="w-3 h-3 rounded-full border-2 border-current animate-pulse" />
                  ) : (
                    <div className="w-3 h-3 rounded-full border-2 border-gray-300" />
                  )}
                  <span>Analyzing page structure</span>
                </div>
                
                <div className={cn(
                  "flex items-center gap-2 transition-all duration-300",
                  discoveryState.progress > 80 ? "text-blue-600" : "text-gray-400"
                )}>
                  {discoveryState.progress > 80 ? (
                    <Check className="w-3 h-3" />
                  ) : discoveryState.progress > 40 ? (
                    <div className="w-3 h-3 rounded-full border-2 border-current animate-pulse" />
                  ) : (
                    <div className="w-3 h-3 rounded-full border-2 border-gray-300" />
                  )}
                  <span>Discovering available links</span>
                </div>
              </div>
            </div>

            {/* Cancel Button */}
            <Button
              variant="outline"
              onClick={cancelDiscovery}
              className="mt-6 px-6 hover:bg-white/80 border-gray-300"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel Discovery
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  /**
   * Render error state
   */
  const renderError = () => {
    if (!discoveryState.error) return null

    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-red-900 mb-1">
                Discovery Failed
              </h3>
              <p className="text-sm text-red-700 mb-4">
                {discoveryState.error}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={retryDiscovery}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleClose}
                  className="border-red-300 text-red-700 hover:bg-red-100"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  /**
   * Render ultra-simple search and actions
   */
  const renderSimpleSearch = () => {
    if (!discoveryState.result) return null

    return (
      <div className="mb-4">
        {/* Simple Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            type="text"
            placeholder="Search for specific pages..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="pl-10 h-12 text-base border-2 border-gray-200 focus:border-blue-500"
          />
        </div>

        {/* Simple Actions Row */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {filteredLinks.length} pages found • {selectedUrls.length} selected
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={selectAllFilteredLinks}
              disabled={filteredLinks.length === 0}
              className="text-sm font-medium border-2 hover:bg-blue-50 hover:border-blue-300"
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={deselectAllLinks}
              disabled={selectedUrls.length === 0}
              className="text-sm font-medium border-2 hover:bg-gray-50 hover:border-gray-300"
            >
              Clear All
            </Button>
          </div>
        </div>
      </div>
    )
  }

  /**
   * Render bulk selection controls
   */
  const renderBulkControls = () => {
    if (!discoveryState.result || filteredLinks.length === 0) return null

    return (
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600">
          {selectedUrls.length} of {filteredLinks.length} links selected
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={selectAllFilteredLinks}
          >
            Select All Filtered
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={deselectAllLinks}
          >
            Deselect All
          </Button>
        </div>
      </div>
    )
  }

  /**
   * Render ultra-simple, clean link item
   */
  const renderSimpleLinkItem = (link: DiscoveredLink) => {
    const isSelected = linkSelection[link.url] || false
    const displayTitle = link.text || link.title || new URL(link.url).pathname.split('/').pop() || 'Untitled'
    const shortUrl = link.url.replace(/^https?:\/\/(www\.)?/, '')

    return (
      <div
        key={link.url}
        className={cn(
          "flex items-center gap-4 p-4 rounded-lg border-2 transition-all cursor-pointer",
          isSelected 
            ? "border-blue-400 bg-blue-50 shadow-md" 
            : "border-gray-200 hover:border-blue-300 hover:bg-gray-50 hover:shadow-sm"
        )}
        onClick={() => toggleLinkSelection(link.url)}
      >
        {/* Large, Clear Checkbox */}
        <div className="flex-shrink-0">
          <div className={cn(
            "w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all",
            isSelected 
              ? "bg-blue-600 border-blue-600 scale-110" 
              : "border-gray-400 hover:border-blue-500 hover:scale-105"
          )}>
            {isSelected && <Check className="w-4 h-4 text-white font-bold" />}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-semibold text-gray-900 mb-1 line-clamp-2">
            {displayTitle}
          </h4>
          <p className="text-sm text-gray-600 truncate">
            {shortUrl}
          </p>
        </div>

        {/* Clear Preview Button */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            window.open(link.url, '_blank', 'noopener,noreferrer')
          }}
          className="flex-shrink-0 px-3 py-2 bg-gray-100 hover:bg-blue-100 rounded-lg transition-colors group border border-gray-300"
          title="Preview this page"
        >
          <div className="flex items-center gap-1">
            <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-blue-600" />
            <span className="text-xs font-medium text-gray-600 group-hover:text-blue-600">Preview</span>
          </div>
        </button>
      </div>
    )
  }

  /**
   * Render clean, spaced links list
   */
  const renderSimpleLinks = () => {
    if (!discoveryState.result || filteredLinks.length === 0) return null

    return (
      <div className="space-y-3">
        {filteredLinks.map(renderSimpleLinkItem)}
      </div>
    )
  }

  /**
   * Render clean, informative header
   */
  const renderSimpleHeader = () => {
    if (!discoveryState.result) return null

    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center shadow-md">
              <Check className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-blue-900 mb-1">
                Found {discoveryState.result.total_found} pages
              </h3>
              <p className="text-blue-700 font-medium">
                Select which pages to add to your chatbot's knowledge base
              </p>
            </div>
          </div>
          <div className="text-center bg-white rounded-lg px-4 py-3 shadow-sm border border-blue-200">
            <div className="text-3xl font-bold text-blue-600">
              {selectedUrls.length}
            </div>
            <div className="text-sm font-medium text-blue-700">Selected</div>
          </div>
        </div>
      </div>
    )
  }

  // ====================================
  // RENDER
  // ====================================

  const hasResults = !!discoveryState.result
  const hasError = !!discoveryState.error
  const isDiscovering = discoveryState.isDiscovering
  const canProceed = hasResults && selectedUrls.length > 0

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      title={isDiscovering ? "" : "Discover & Select Links"}
      description={!isDiscovering && selectedChatbot ? `Choose which links to process from ${initialUrl} for ${selectedChatbot.name}` : !isDiscovering ? `Choose which links to process from ${initialUrl}` : ""}
      showCloseButton={!isDiscovering}
    >
      {/* Progress State */}
      {isDiscovering && renderProgress()}
      
      {/* Main Content - Only show when not discovering */}
      {!isDiscovering && (
        <div className="flex flex-col h-full max-h-[calc(90vh-8rem)]">
          {/* Fixed Header Content */}
          <div className="flex-shrink-0">
            {/* Error State */}
            {hasError && renderError()}
            
            {/* Simplified Header and Search */}
            {hasResults && (
              <>
                {renderSimpleHeader()}
                {renderSimpleSearch()}
              </>
            )}
          </div>

          {/* Scrollable Links Content - takes remaining space */}
          {hasResults && filteredLinks.length > 0 && (
            <div className="flex-1 overflow-y-auto min-h-0 mb-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
              <div className="pr-2"> {/* Padding for scrollbar */}
                {renderSimpleLinks()}
              </div>
            </div>
          )}

          {/* Empty State - Clear and Helpful */}
          {hasResults && filteredLinks.length === 0 && (
            <div className="flex-1 flex items-center justify-center min-h-[200px]">
              <div className="text-center max-w-sm">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No pages found
                </h3>
                <p className="text-gray-600 mb-4">
                  Your search didn't match any discovered pages.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setFilters(DEFAULT_FILTERS)}
                  className="px-6 py-2 border-2 border-blue-300 text-blue-700 hover:bg-blue-50 font-medium"
                >
                  Show All Pages
                </Button>
              </div>
            </div>
          )}

          {/* Footer Actions - Always Visible at Bottom */}
          {hasResults && !hasError && (
            <div className="flex-shrink-0 bg-white border-t-2 border-gray-200 px-6 py-6 mt-4 -mx-8 mb-0">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="px-6 py-3 text-base font-medium border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                >
                  Cancel
                </Button>
                
                <div className="flex items-center gap-4">
                  <div className="text-base font-medium text-gray-700 bg-gray-100 px-4 py-2 rounded-lg">
                    {selectedUrls.length} page{selectedUrls.length !== 1 ? 's' : ''} selected
                  </div>
                  <Button
                    onClick={handleConfirmSelection}
                    disabled={!canProceed}
                    className={cn(
                      "px-8 py-3 text-base font-bold text-white rounded-lg transition-all shadow-md",
                      canProceed
                        ? "bg-blue-600 hover:bg-blue-700 hover:shadow-lg transform hover:scale-105"
                        : "bg-gray-400 cursor-not-allowed"
                    )}
                  >
                    {canProceed ? (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Process Selected Pages
                      </>
                    ) : (
                      "Select Pages to Continue"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

export default LinkDiscoveryModal
